import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Users, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SupervisorDashboard() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState([]);
  const [benchmarks, setBenchmarks] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [empRes, statsRes, benchRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/for-supervisor`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/stats/employees`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/benchmarks`, { headers: getAuthHeader() })
      ]);
      setEmployees(empRes.data);
      setStats(statsRes.data);
      setBenchmarks(benchRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const calculateYTDStats = (userId) => {
    // Match Admin Dashboard: For current year YTD, only include completed months
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // 1-12
    
    const userStats = stats.filter(s => {
      if (s.user_id !== userId || s.year !== selectedYear) return false;
      // For current year: only include completed months (not the current month)
      if (selectedYear === currentYear) {
        return s.month < currentMonth;
      }
      return true; // For past years, include all months
    });
    
    if (userStats.length === 0) return null;

    const totals = userStats.reduce((acc, s) => ({
      billable_hours: acc.billable_hours + (s.billable_hours || 0),
      total_hours: acc.total_hours + (s.total_hours || 0),
      plt_hours: acc.plt_hours + (s.plt_hours || 0),
      billed_amount: acc.billed_amount + (s.billed_amount || 0),
      realized_amount: acc.realized_amount + (s.realized_amount || 0),
      total_pay: acc.total_pay + (s.total_pay || 0),
    }), { billable_hours: 0, total_hours: 0, plt_hours: 0, billed_amount: 0, realized_amount: 0, total_pay: 0 });

    // Match Admin Dashboard calculations exactly:
    // Billable % = Billable Hours / (Total Hours - PLT Hours)
    const workingHours = totals.total_hours - totals.plt_hours;
    const billable_percent = workingHours > 0 ? (totals.billable_hours / workingHours) * 100 : 0;
    
    // Realization % = Realized Amount / Billed Amount
    const realization_percent = totals.billed_amount > 0 ? (totals.realized_amount / totals.billed_amount) * 100 : 0;
    
    // Bill to Pay % = Realized Amount / Total Pay (NOT billed_amount)
    const bill_to_pay_percent = totals.total_pay > 0 ? (totals.realized_amount / totals.total_pay) * 100 : 0;

    return {
      billable_percent,
      realization_percent,
      bill_to_pay_percent,
    };
  };

  // Calculate overall score based on benchmarks (EXACT same weighted formula as Admin Dashboard)
  const calculateOverallScore = (ytd) => {
    if (!ytd || !benchmarks) return 0;
    
    // Calculate percentage of target achieved for each metric
    const billablePctOfTarget = benchmarks.billable_target > 0 ? (ytd.billable_percent / benchmarks.billable_target) * 100 : 0;
    const realizationPctOfTarget = benchmarks.realization_target > 0 ? (ytd.realization_percent / benchmarks.realization_target) * 100 : 0;
    const billToPayPctOfTarget = benchmarks.bill_to_pay_target > 0 ? (ytd.bill_to_pay_percent / benchmarks.bill_to_pay_target) * 100 : 0;
    
    // Weighted score - Bill to Pay 50%, Billable 30%, Realization 20%
    let overallScore = (billToPayPctOfTarget * 0.50) + (billablePctOfTarget * 0.30) + (realizationPctOfTarget * 0.20);
    
    // Cap rules (EXACT same as Admin Dashboard) - can't hide weak metrics behind strong ones
    // If ANY metric is below 70% of target → cap at 74 (Needs Focus)
    if (billToPayPctOfTarget < 70 || billablePctOfTarget < 70 || realizationPctOfTarget < 70) {
      overallScore = Math.min(overallScore, 74);
    }
    // If Bill to Pay (most important) is below 80% of target → cap at 89 (Close)
    else if (billToPayPctOfTarget < 80) {
      overallScore = Math.min(overallScore, 89);
    }
    
    return overallScore.toFixed(0); // Changed to match Admin (no decimal)
  };

  // Get status badge based on overall score (same as Admin Dashboard)
  const getStatusBadge = (score) => {
    const numScore = parseFloat(score);
    if (numScore >= 100) {
      return { label: "Exceeds", className: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" };
    } else if (numScore >= 90) {
      return { label: "On Track", className: "bg-blue-500/20 text-blue-400 border border-blue-500/30" };
    } else if (numScore >= 75) {
      return { label: "Close", className: "bg-amber-500/20 text-amber-400 border border-amber-500/30" };
    } else {
      return { label: "Needs Focus", className: "bg-rose-500/20 text-rose-400 border border-rose-500/30" };
    }
  };

  const meetsBenchmark = (value, target) => {
    if (!value || !target) return false;
    return value >= target;
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return "-";
    return `${value.toFixed(1)}%`;
  };

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear];

  // Build employee stats with calculated metrics (matching Admin Dashboard exactly)
  const employeeStats = employees
    .map(emp => {
      const ytd = calculateYTDStats(emp.id);
      const overallScore = calculateOverallScore(ytd);
      return {
        ...emp,
        ytd,
        overallScore,
        meetsBillable: meetsBenchmark(ytd?.billable_percent, benchmarks?.billable_target),
        meetsRealization: meetsBenchmark(ytd?.realization_percent, benchmarks?.realization_target),
        meetsBillToPay: meetsBenchmark(ytd?.bill_to_pay_percent, benchmarks?.bill_to_pay_target),
      };
    })
    // Filter out specific users that shouldn't appear in stats (same as Admin Dashboard)
    .filter(emp => !["Kathy Henson", "Tonia McKenzie"].includes(emp.name))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-6" data-testid="supervisor-dashboard">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Employee Stats</h1>
          <p className="text-muted-foreground">Monitor employee performance metrics</p>
        </div>
        <Select value={selectedYear.toString()} onValueChange={v => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map(y => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Benchmarks Reference */}
      {benchmarks && (
        <Card className="bg-amber-500/10 border-amber-200">
          <CardContent className="py-3">
            <div className="flex items-center gap-6 text-sm">
              <span className="font-medium text-amber-800">Employee Benchmarks:</span>
              <span>Billable: <strong>{benchmarks.billable_target}%</strong></span>
              <span>Realization: <strong>{benchmarks.realization_target}%</strong></span>
              <span>Bill-to-Pay: <strong>{benchmarks.bill_to_pay_target}%</strong></span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employee Stats Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {selectedYear} YTD Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Billable %</TableHead>
                  <TableHead className="text-right">Realization %</TableHead>
                  <TableHead className="text-right">Bill to Pay %</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employeeStats.map(emp => {
                  const status = getStatusBadge(emp.overallScore);
                  return (
                    <TableRow key={emp.id}>
                      <TableCell className="font-medium">{emp.name}</TableCell>
                      <TableCell className={`text-right font-mono ${emp.meetsBillable ? "text-emerald-500" : "text-rose-600"}`}>
                        {formatPercent(emp.ytd?.billable_percent)}
                      </TableCell>
                      <TableCell className={`text-right font-mono ${emp.meetsRealization ? "text-emerald-500" : "text-rose-600"}`}>
                        {formatPercent(emp.ytd?.realization_percent)}
                      </TableCell>
                      <TableCell className={`text-right font-mono ${emp.meetsBillToPay ? "text-emerald-500" : "text-rose-600"}`}>
                        {formatPercent(emp.ytd?.bill_to_pay_percent)}
                      </TableCell>
                      <TableCell className="text-center">
                        {emp.ytd ? (
                          <span 
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                            title={`Overall score: ${emp.overallScore}% of benchmarks`}
                          >
                            {status.label}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {employeeStats.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No employees found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
